import type { PricingPlan } from "@/domain/city";
import type { PaginatedResponse } from "@/v2/queries/types";
import { apiClient } from "../apiClient";

export type { PaginatedResponse };

export interface City {
  id: number;
  name: string;
  code: string;
  color: string;
  status: string;
  closeReason?: string;
  vat: number;
  latitude?: number;
  longitude?: number;
  label?: string;
  value?: number;
  whiteLabelId?: number | null;
  company?: {
    name?: string;
    address?: string;
    phone?: string;
    email?: string;
  };
  bikeIconUrl?: string;
  electricBikeIconUrl?: string;
  scooterIconUrl?: string;
  cargoBikeIconUrl?: string;
  iconUrl?: string;
  privacyUrl?: string;
  contactPhone?: string;
  contactAddress?: string;
  howItWorksUrl?: string;
  domains?: string;
  termsUrl?: string;
  supportUrl?: string;
  faqUrl?: string;
  operationalHourStartsAt?: string;
  operationalHourEndsAt?: string;
  supportEmail?: string;
  photoFinish?: boolean;
  qrStationVerificationEnabled?: boolean;
  qrStationVerificationDistanceThresholdMeters?: number | null;
  mailVerificationRequired?: boolean;
  timezone?: string;
  publicAccessEnabled?: boolean;
  rentalUri?: {
    android?: { discovery_uri?: string; store_uri?: string };
    ios?: { discovery_uri?: string; store_uri?: string };
  };
}

export interface CityCreate {
  name: string;
  code: string;
  color?: string;
  status?: string;
  vat?: number;
}

export interface CityUpdate {
  name?: string;
  code?: string;
  color?: string;
  status?: string;
  vat?: number;
}

export interface CityListParams {
  page?: number;
  size?: number;
  search?: string;
  sort?: string;
  filter?: string;
}

export interface PricingPlansResponse {
  currentPlans: PricingPlan[];
  draftPlans: PricingPlan[];
}

/** Form field configuration for pricing plan creation/editing */
export interface PricingPlanField {
  name: string;
  type: string;
  label?: string;
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  [key: string]: unknown;
}

export interface PricingPlanFieldsResponse {
  fields: PricingPlanField[];
  [key: string]: unknown;
}

/** DTO for creating/updating a pricing plan */
export interface PricingPlanDto {
  cityId: number;
  type: string;
  description?: string;
  freePeriodDescription?: string;
  active?: boolean;
  cancelable?: boolean;
  disabled?: boolean;
  bookingSupported?: boolean;
  products?: Array<{
    vehicleType: string;
    status: string;
    startupAmount?: number;
    maxAmount?: number;
    depositAmount?: number;
    minutesFree?: number;
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}

export async function getCities(
  params: CityListParams
): Promise<PaginatedResponse<City>> {
  const { page = 0, size = 10, search = "", sort = "", filter = "" } = params;

  const response = await apiClient.get("/station-service/dashboard/cities", {
    params: {
      page,
      size,
      search,
      sort,
      filter,
    },
  });

  return response.data;
}

export async function getCityById(id: number): Promise<City> {
  const response = await apiClient.get(
    `/station-service/dashboard/cities/${id}`
  );
  return response.data;
}

export async function createCity(dto: CityCreate): Promise<City> {
  const response = await apiClient.post(
    "/station-service/dashboard/cities",
    dto
  );
  return response.data;
}

export async function updateCity(id: number, dto: CityUpdate): Promise<City> {
  const response = await apiClient.put(
    `/station-service/dashboard/cities/${id}`,
    dto
  );
  return response.data;
}

export async function getPricingPlans(
  cityId: number
): Promise<PricingPlansResponse> {
  const response = await apiClient.get(
    "/payment-service/dashboard/v2/user/settings/cities/plans",
    {
      params: { cityId },
    }
  );
  return response.data;
}

export async function getPricingPlanFields(): Promise<PricingPlanFieldsResponse> {
  const response = await apiClient.get(
    "/payment-service/dashboard/pricing/plan/fields"
  );
  return response.data;
}

export async function deletePricingPlan(
  id: number,
  isDraft: boolean
): Promise<void> {
  const endpoint = isDraft
    ? `/payment-service/dashboard/pricing/draft/${id}`
    : `/payment-service/dashboard/pricing/plan/${id}`;

  await apiClient.delete(endpoint);
}

export async function getPricingPlanById(planId: number): Promise<PricingPlan> {
  const response = await apiClient.get(
    `/payment-service/dashboard/pricing/plan/${planId}`
  );
  return response.data;
}

export async function getDraftPricingPlanById(
  planId: number
): Promise<PricingPlan> {
  const response = await apiClient.get(
    `/payment-service/dashboard/pricing/draft/${planId}`
  );
  return response.data;
}

export async function createPricingPlan(
  data: PricingPlanDto
): Promise<PricingPlan> {
  const response = await apiClient.post(
    "/payment-service/dashboard/pricing/plan",
    data
  );
  return response.data;
}

export async function updatePricingPlan(
  planId: number,
  data: Partial<PricingPlanDto>
): Promise<PricingPlan> {
  const response = await apiClient.put(
    `/payment-service/dashboard/pricing/plan/${planId}`,
    data
  );
  return response.data;
}

export interface Whitelabel {
  id: number;
  name: string;
  code: string;
  [key: string]: unknown;
}

export async function getWhiteLabels(): Promise<Whitelabel[]> {
  const response = await apiClient.get(
    "/station-service/dashboard/whitelabels"
  );
  return response.data;
}

export interface PricingManagementResponse {
  plans: Array<{
    plan: PricingPlanDto;
    status: "ACTIVE" | "INACTIVE";
    scheduledEndMillis: number | null;
  }>;
  drafts: PricingPlanDto[];
}

export async function getPricingManagement(
  cityId: number
): Promise<PricingManagementResponse> {
  return (
    await apiClient.get("/payment-service/dashboard/pricing/plans", {
      params: { cityId },
    })
  ).data;
}

export async function endPricingPlan(
  id: number,
  endAtMillis: number | null
): Promise<void> {
  await apiClient.post(`/payment-service/dashboard/pricing/plan/${id}/end`, {
    endAtMillis,
  });
}

export async function cancelPricingPlanEnd(id: number): Promise<void> {
  await apiClient.delete(`/payment-service/dashboard/pricing/plan/${id}/end`);
}
